import { useState, useEffect } from 'react';
import { Link2, RotateCcw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useAccountBindings, useAddBinding, useSwitchBinding } from '@/lib/api/accounts-queries';
import { formatDate, todayIsoDate, tomorrowIsoDate } from '@/lib/datetime';
import { useToast } from '@/lib/hooks/use-toast';
import type { AdsAccount, AppPackageVO } from '@/lib/api/types';
import { cn } from '@/lib/utils';

export function BindingDialog({
  account,
  apps,
  onClose,
}: {
  account: AdsAccount | null;
  apps: AppPackageVO[];
  onClose: () => void;
}) {
  const isSwitch = account?.activeAppPkg != null;
  const [pkg, setPkg] = useState('');
  const [startDate, setStartDate] = useState(() => (isSwitch ? tomorrowIsoDate() : todayIsoDate()));
  const { toast } = useToast();
  const addBinding = useAddBinding();
  const switchBinding = useSwitchBinding();

  const { data: bindings, isLoading: bindingsLoading } = useAccountBindings(
    account?.accountId ?? null,
  );

  const accountId = account?.accountId;
  useEffect(() => {
    if (accountId) {
      setPkg('');
      setStartDate(account?.activeAppPkg ? tomorrowIsoDate() : todayIsoDate());
    }
  }, [accountId, account?.activeAppPkg]);

  const dateError =
    isSwitch && startDate && startDate <= todayIsoDate()
      ? '当前已有 App 绑定，同一天不能投放两个账户，请选择明天或之后的日期'
      : null;

  const isPending = addBinding.isPending || switchBinding.isPending;

  const handleSubmit = () => {
    if (!account || !pkg || dateError) return;
    const mutation = isSwitch ? switchBinding : addBinding;
    mutation.mutate(
      { accountId: account.accountId, pkg, startDate },
      {
        onSuccess: () => {
          toast({ description: isSwitch ? 'App 已切换' : 'App 已绑定' });
          onClose();
        },
        onError: (err) => {
          toast({ variant: 'destructive', description: err.message });
        },
      },
    );
  };

  const bindingRecords = Array.isArray(bindings) ? bindings : [];

  return (
    <Dialog open={!!account} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isSwitch ? (
              <RotateCcw className="h-4 w-4 text-purple-600" />
            ) : (
              <Link2 className="h-4 w-4 text-blue-600" />
            )}
            {isSwitch ? '切换绑定 App' : '绑定 App'}
          </DialogTitle>
          <DialogDescription>
            {account?.accountName} · <span className="font-mono">{account?.accountCode}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border p-3">
          {isSwitch && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">当前绑定</span>
              <span className="font-mono text-xs">
                {account?.activeAppName || account?.activeAppPkg}
              </span>
            </div>
          )}
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">
              {isSwitch ? '切换到' : '选择 App'}
            </Label>
            <Select value={pkg} onValueChange={setPkg}>
              <SelectTrigger>
                <SelectValue placeholder="选择 App" />
              </SelectTrigger>
              <SelectContent>
                {apps.map((a) => (
                  <SelectItem key={a.pkg} value={a.pkg}>
                    {a.name}{' '}
                    <span className="font-mono text-xs text-muted-foreground">({a.pkg})</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">绑定开始日期</Label>
            <Input
              type="date"
              value={startDate}
              min={isSwitch ? tomorrowIsoDate() : undefined}
              onChange={(e) => setStartDate(e.target.value)}
              className={cn(dateError && 'border-destructive')}
            />
            {dateError ? (
              <p className="text-[12px] text-destructive">{dateError}</p>
            ) : isSwitch ? (
              <p className="text-[11px] text-muted-foreground">
                原绑定结束日期将自动更新为所选日期的前一天
              </p>
            ) : null}
          </div>
        </div>

        <div className="space-y-2">
          <p className="text-sm font-medium">绑定历史</p>
          <ScrollArea className="h-44 rounded-md border">
            {bindingsLoading ? (
              <div className="space-y-2 p-3">
                {[1, 2, 3].map((i) => (
                  <Skeleton key={i} className="h-8 w-full" />
                ))}
              </div>
            ) : bindingRecords.length === 0 ? (
              <div className="flex h-44 items-center justify-center text-sm text-muted-foreground">
                暂无绑定记录
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 border-b bg-background">
                  <tr className="text-xs text-muted-foreground">
                    <th className="p-2 text-left font-normal">App</th>
                    <th className="p-2 text-left font-normal">开始日期</th>
                    <th className="p-2 text-left font-normal">结束日期</th>
                  </tr>
                </thead>
                <tbody>
                  {bindingRecords.map((b) => (
                    <tr key={b.id} className="border-b last:border-0 hover:bg-muted/40">
                      <td className="p-2 font-mono text-xs">{b.pkg || b.app}</td>
                      <td className="p-2 tabular-nums text-muted-foreground">{formatDate(b.startDate)}</td>
                      <td className="p-2 tabular-nums text-muted-foreground">
                        {b.endDate ? formatDate(b.endDate) : (
                          <span className="text-xs font-medium text-success">进行中</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </ScrollArea>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            取消
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={
              !pkg ||
              !!dateError ||
              isPending ||
              (isSwitch && pkg === account?.activeAppPkg)
            }
          >
            {isPending
              ? isSwitch
                ? '切换中…'
                : '绑定中…'
              : isSwitch
                ? '确认切换'
                : '确认绑定'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
